import React, { useContext, useState, useEffect, useCallback } from "react";
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Selection,
  Inject,
  Sort,
  Edit,
  ExcelExport,
  PdfExport,
  Toolbar,
  Aggregate,
  AggregateColumnsDirective,
  AggregateColumnDirective,
  AggregateDirective,
  AggregatesDirective,
  Page,
  Search,
} from "@syncfusion/ej2-react-grids";
import { useNavigate } from "react-router-dom";
import { GlobalVendorContex } from "./Firebase";
import PartnerInfo from "./pages/PartnerManagement/PartnerInfo";
import FirebaseContext from "./Firebase/context";
import { AuthUserContext } from "./Firebase/session";
import AddPartner from "./pages/AddPartner/AddPartner";
import { SwitchComponent } from "@syncfusion/ej2-react-buttons";
import CompanyLinkTemplate from "./grid_templates/CompanyLinkTemplate";
import LinkTemplate from "./grid_templates/LinkTemplate";

const PartnersManagement = () => {
  const firebase = useContext(FirebaseContext);
  const authUser = useContext(AuthUserContext);
  const vendorsData = useContext(GlobalVendorContex);
  const navigate = useNavigate();

  // State
  const [isVendorSelected, setIsVendorSelected] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [grid, setGrid] = useState(null);
  const [allVendors, setAllVendors] = useState([]);
  const [showMigrateDialog, setShowMigrateDialog] = useState(false);
  const [fetchActiveVendors, setFetchActiveVendors] = useState(true);

  // Memoized toolbar options
  const toolBarOptions = React.useMemo(
    () => [
      "Search",
      {
        text: "View Vendor",
        tooltipText: "View Vendor",
        prefixIcon: "e-view",
        id: "view_vendor",
      },
    ],
    []
  );

  // Callback to update selection states
  const onRowSelect = useCallback((args) => {
    if (args?.data) {
      setIsVendorSelected(true);
      setSelectedVendor(args.data);
    }
  }, []);

  const onRowDeselect = useCallback(() => {
    setIsVendorSelected(false);
    setSelectedVendor(null);
  }, []);

  // Toolbar click handler
  const toolbarClicked = useCallback(
    (args) => {
      switch (args.item.text) {
        case "PDF Export":
          grid?.pdfExport();
          break;
        case "Excel Export":
          grid?.excelExport();
          break;
        case "View Vendor":
          if (selectedVendor) {
            vendorsData.setSearchedVendId(selectedVendor.id);
            vendorsData.setIsResetted(false);
            navigate("/");
          }
          break;
        default:
          break;
      }
    },
    [grid, selectedVendor, vendorsData, navigate]
  );

  // Footer template functions (memoized)
  const footerSum = useCallback((props) => <span>Total: {props.Sum}</span>, []);
  const footerAvg = useCallback(
    (props) => <span>Average: {props.Average}</span>,
    []
  );
  const customFooter = useCallback(
    () => <span>{allVendors.length} Vendors</span>,
    [allVendors.length]
  );

  // Helper to fetch the vendor slug from /vendors/{vendorId}/partnerPublic/profile
  const fetchVendorSlug = async (vendorId) => {
    try {
      const profileDoc = await firebase.db
        .collection("vendors")
        .doc(vendorId)
        .collection("partnerPublic")
        .doc("profile")
        .get();
      return profileDoc.exists ? profileDoc.data()?.slug || "" : "";
    } catch (error) {
      console.error("Error fetching vendor slug for", vendorId, error);
      return "";
    }
  };

  // Subscribe to vendors collection and map the data.
  useEffect(() => {
    let unsubscribe;
    const getVendors = () => {
      if (authUser.isSuperPartner && authUser.vendor) {
        let vendorsQuery = firebase.db.collection("vendors");
        if (fetchActiveVendors) {
          vendorsQuery = vendorsQuery.where(
            "activePartner",
            "==",
            fetchActiveVendors
          );
        }
        unsubscribe = vendorsQuery
          .orderBy("addedOn", "desc")
          .onSnapshot((querySnapshot) => {
            Promise.all(
              querySnapshot.docs.map(async (vend) => {
                const vendorData = vend.data();
                const {
                  companyName,
                  lastLogin,
                  vendorProfileProgress,
                  settings,
                  marketCount,
                  caseStudyCount,
                  priceCardCount,
                  ...data
                } = vendorData;

                // Calculate case studies count for the given statuses
                const caseStudies = caseStudyCount
                  ? (caseStudyCount["DRAFT"] || 0) +
                    (caseStudyCount["FEATURED"] || 0) +
                    (caseStudyCount["READY-TO-SUBMIT"] || 0) +
                    (caseStudyCount["REVIEWED"] || 0)
                  : 0;

                const marketCountNo = marketCount
                  ? Object.values(marketCount).reduce((a, b) => a + b, 0)
                  : 0;
                const priceCardCountNo = priceCardCount
                  ? Object.values(priceCardCount).reduce((a, b) => a + b, 0)
                  : 0;

                let vendorProgress = 0;
                if (vendorProfileProgress) {
                  vendorProgress =
                    (vendorProfileProgress.devsProgress || 0) * 0.75 +
                    (vendorProfileProgress.financialProgress || 0) * 0.25;
                }

                let theTrustScore = 0;
                if (
                  settings?.generalService &&
                  settings.generalService.length
                ) {
                  theTrustScore =
                    settings.generalService.reduce(
                      (acc, item) => acc + (item?.trustScore || 0),
                      0
                    ) / settings.generalService.length;
                }

                let logoutDays = 999;
                if (lastLogin) {
                  const logoutDate = lastLogin.toDate();
                  const today = new Date();
                  const diffTime = today.getTime() - logoutDate.getTime();
                  logoutDays = Math.max(
                    0,
                    Math.floor(diffTime / (1000 * 3600 * 24))
                  );
                }

                // Get the vendor slug from the partner public profile
                const vendorSlug = await fetchVendorSlug(vend.id);
                const partnerPage = `https://sprytelabs.com/partners/${vendorSlug}`;

                return {
                  name: companyName || "no name",
                  id: vend.id,
                  vendorRef: vend.ref,
                  ...data,
                  lastLogin: lastLogin
                    ? lastLogin.toDate().toDateString()
                    : "No Login Time",
                  totalDevs: data.totalDevs || 0,
                  addedOn: data.addedOn ? data.addedOn.toDate() : new Date(),
                  totalDevsLive: data.totalDevsLive || 0,
                  bookableCount: data.bookableCount || 0,
                  caseStudies,
                  marketCountNo,
                  marketCount,
                  priceCardCountNo,
                  priceCardCount,
                  lastLogOut: logoutDays,
                  caseStudyCount,
                  profileCompletion: vendorProgress,
                  trustScore: theTrustScore.toFixed(2),
                  ratingFetched: data.rating || 0,
                  vendorSlug,
                  partnerPage,
                };
              })
            )
              .then((vendors) => setAllVendors(vendors))
              .catch((error) =>
                console.error("Error processing vendors", error)
              );
          });
      } else {
        setAllVendors([]);
      }
    };

    getVendors();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [authUser, firebase.db, fetchActiveVendors, firebase]);

  // Export event handler for Excel export to show the full link
  const excelQueryCellInfo = useCallback((args) => {
    if (args.column.field === "vendorSlug") {
      // Modify the cell value during Excel export.
      args.value = `https://sprytelabs.com/partners/${args.value}`;
    }
  }, []);

  // Export event handler for PDF export to show the full link
  const pdfQueryCellInfo = useCallback((args) => {
    if (args.column.field === "vendorSlug") {
      // Modify the cell data during PDF export.
      args.data = `https://sprytelabs.com/partners/${args.data}`;
    }
  }, []);

  return (
    <div className="partnManagement" style={{ paddingBottom: "50px" }}>
      <div
        className="resource-header"
        style={{ marginBottom: "20px", display: "flex" }}
      >
        <div className="header-intro">
          <h3>Spryte Partners</h3>
          <p className="lighter">
            Registered Spryte partner account information
          </p>
        </div>
        <div
          style={{
            display: "flex",
            gap: "10px",
            marginLeft: "auto",
            justifyContent: "end",
            alignItems: "center",
          }}
        >
          <p>All Vendors</p>
          <SwitchComponent
            id="isActivePartnerAllPartneresPage"
            checked={fetchActiveVendors}
            change={(e) => setFetchActiveVendors(e.checked)}
          />
          <p>Active Vendors</p>
        </div>
        <AddPartner
          openState={showMigrateDialog}
          setOpenState={setShowMigrateDialog}
        />
      </div>

      <PartnerInfo selectedVendor={selectedVendor} />
      <div className="partnManagement_body-grid control-pane">
        <GridComponent
          ref={(g) => setGrid(g)}
          dataSource={allVendors}
          allowSelection
          allowSorting
          height="auto"
          toolbar={
            isVendorSelected
              ? toolBarOptions
              : ["Search", "ExcelExport", "PdfExport"]
          }
          rowSelected={onRowSelect}
          rowDeselected={onRowDeselect}
          allowPdfExport
          allowExcelExport
          toolbarClick={toolbarClicked}
          excelQueryCellInfo={excelQueryCellInfo}
          pdfQueryCellInfo={pdfQueryCellInfo}
          allowPaging
          pageSettings={{
            pageSize: 20,
            pageCount: 10,
            enableQueryString: true,
            pageSizes: true,
          }}
        >
          <ColumnsDirective>
            <ColumnDirective
              field="name"
              headerText="Company"
              // textAlign="Center"
              width="220"
            />
            <ColumnDirective
              field="website"
              headerText="Domain"
              // textAlign="Center"
              width="250"
              template={LinkTemplate}
            />
            <ColumnDirective
              field="partnerPage"
              headerText="Partner Page"
              width="100"
              // textAlign="Center"
              template={CompanyLinkTemplate}
            />
            <ColumnDirective
              field="totalDevs"
              headerText="Roster size"
              format="n0"
              width="100"
              defaultValue={0}
              textAlign="Center"
            />
            <ColumnDirective
              field="totalDevsLive"
              headerText="Resources"
              format="n0"
              width="95"
              defaultValue={0}
              textAlign="Center"
            />
            <ColumnDirective
              field="bookableCount"
              headerText="Bookable"
              format="n0"
              width="100"
              textAlign="Center"
            />
            <ColumnDirective
              field="ratingFetched"
              headerText="Rating"
              format="n0"
              width="120"
              textAlign="Center"
            />
            <ColumnDirective
              field="trustScore"
              headerText="Trust Score"
              defaultValue={0}
              format="n0"
              width="120"
              textAlign="Center"
            />
            <ColumnDirective
              field="profileCompletion"
              headerText="Profile Completion"
              defaultValue={0}
              editType="numericedit"
              format="p2"
              required={false}
              width="160"
              textAlign="Center"
            />
            <ColumnDirective
              field="priceCardCountNo"
              headerText="Rate Cards"
              format="n0"
              width="100"
              defaultValue={0}
              textAlign="Center"
            />
            <ColumnDirective
              field="marketCountNo"
              headerText="Markets"
              format="n0"
              defaultValue={0}
              width="80"
              textAlign="Center"
            />
            <ColumnDirective
              field="caseStudies"
              headerText="Case Studies"
              format="n0"
              width="100"
              defaultValue={0}
              textAlign="Center"
            />
            <ColumnDirective
              field="clientLogoCount"
              headerText="Client Logos"
              format="n0"
              width="100"
              defaultValue={0}
              textAlign="Center"
            />
            <ColumnDirective
              field="lastLogOut"
              headerText="Last Login"
              width="100"
              format="n0"
              defaultValue={0}
              textAlign="Center"
            />
            <ColumnDirective
              field="addedOn"
              headerText="Added On"
              editType="datepickeredit"
              width="150"
              textAlign="Center"
              format="yMd"
            />
          </ColumnsDirective>

          <AggregatesDirective>
            <AggregateDirective>
              <AggregateColumnsDirective>
                <AggregateColumnDirective
                  field="name"
                  type="Custom"
                  footerTemplate={customFooter}
                />
                <AggregateColumnDirective
                  field="totalDevs"
                  type="Sum"
                  format="N0"
                  footerTemplate={footerSum}
                />
                <AggregateColumnDirective
                  field="totalDevsLive"
                  type="Sum"
                  format="N0"
                  footerTemplate={footerSum}
                />
                <AggregateColumnDirective
                  field="bookableCount"
                  type="Sum"
                  format="N0"
                  footerTemplate={footerSum}
                />
                <AggregateColumnDirective
                  field="trustScore"
                  type="Average"
                  format="N2"
                  footerTemplate={footerAvg}
                />
                <AggregateColumnDirective
                  field="profileCompletion"
                  type="Average"
                  format="P2"
                  textAlign="Left"
                  footerTemplate={footerAvg}
                />
                <AggregateColumnDirective
                  field="priceCardCountNo"
                  type="Sum"
                  format="N0"
                  textAlign="Left"
                  footerTemplate={footerSum}
                />
                <AggregateColumnDirective
                  field="marketCountNo"
                  type="Sum"
                  format="N0"
                  textAlign="Left"
                  footerTemplate={footerSum}
                />
                <AggregateColumnDirective
                  field="caseStudies"
                  type="Sum"
                  format="N0"
                  textAlign="Left"
                  footerTemplate={footerSum}
                />
                <AggregateColumnDirective
                  field="clientLogoCount"
                  type="Sum"
                  format="N0"
                  textAlign="Left"
                  footerTemplate={footerSum}
                />
              </AggregateColumnsDirective>
            </AggregateDirective>
          </AggregatesDirective>
          <Inject
            services={[
              Selection,
              Aggregate,
              ExcelExport,
              PdfExport,
              Toolbar,
              Edit,
              Sort,
              Page,
              Search,
            ]}
          />
        </GridComponent>
      </div>
    </div>
  );
};

export default PartnersManagement;
